import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  DatePicker,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, WalletOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  couponApplyScopeMap,
  couponApplyScopeOptions,
  couponColorMap,
  couponColorOptions,
  couponEffectiveTypeOptions,
  couponReceiveMethodMap,
  couponReceiveMethodOptions,
  couponThresholdOptions,
  couponTypeMap,
  couponTypeOptions,
  createServiceCoupon,
  deleteServiceCoupon,
  getServiceCoupons,
  type CouponApplyScope,
  type CouponReceiveMethod,
  type ServiceCoupon as ServiceCouponRecord,
  type SaveServiceCouponRequest,
  updateServiceCoupon,
} from '../../services/serviceCouponService';
import { getAllServiceCategories, type ServiceCategory } from '../../services/serviceCategoryService';
import { getServiceProducts, type ServiceProduct } from '../../services/serviceProductService';

const { RangePicker } = DatePicker;

interface CouponFormValues extends SaveServiceCouponRequest {
  fixedTimeRange?: [dayjs.Dayjs, dayjs.Dayjs];
}

const defaultFormValues: Partial<CouponFormValues> = {
  color: 'blue',
  receiveMethod: 'manual',
  couponType: 'cash',
  discountAmount: 0,
  discountRate: 0,
  thresholdType: 'none',
  minOrderAmount: 0,
  applyScope: 'all',
  categoryIds: [],
  productIds: [],
  effectiveType: 'immediate',
  validDays: 30,
  totalQuantity: 0,
  receivedQuantity: 0,
  sort: 0,
  status: 1,
};

const flattenCategories = (list: ServiceCategory[]): ServiceCategory[] => {
  const result: ServiceCategory[] = [];
  const walk = (items: ServiceCategory[]) => {
    items.forEach((item) => {
      result.push(item);
      if (item.children?.length) {
        walk(item.children);
      }
    });
  };
  walk(list);
  return result;
};

const ServiceCouponPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [data, setData] = useState<ServiceCouponRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [keyword, setKeyword] = useState('');
  const [receiveMethod, setReceiveMethod] = useState<CouponReceiveMethod | undefined>();
  const [applyScope, setApplyScope] = useState<CouponApplyScope | undefined>();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ServiceCouponRecord | null>(null);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [products, setProducts] = useState<ServiceProduct[]>([]);
  const [form] = Form.useForm<CouponFormValues>();

  const currentCouponType = Form.useWatch('couponType', form);
  const currentThresholdType = Form.useWatch('thresholdType', form);
  const currentApplyScope = Form.useWatch('applyScope', form);
  const currentEffectiveType = Form.useWatch('effectiveType', form);

  const categoryOptions = useMemo(
    () => flattenCategories(categories).map(item => ({ label: item.name, value: item.id })),
    [categories],
  );

  const productOptions = useMemo(
    () => products.map(item => ({ label: item.categoryName ? `${item.name}（${item.categoryName}）` : item.name, value: item.id })),
    [products],
  );

  const loadData = async (nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    try {
      const res = await getServiceCoupons({
        page: nextPage,
        pageSize: nextPageSize,
        keyword: keyword || undefined,
        receiveMethod,
        applyScope,
      });
      setData(res.data?.list || []);
      setTotal(res.data?.total || 0);
    } catch {
      message.error('获取优惠券失败');
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [categoryRes, productRes] = await Promise.all([
        getAllServiceCategories(),
        getServiceProducts({ page: 1, pageSize: 100, status: 1 }),
      ]);
      setCategories(categoryRes.data || []);
      setProducts(productRes.data?.list || []);
    } catch {
      message.error('加载分类或商品失败');
    }
  };

  useEffect(() => {
    loadData();
  }, [page, pageSize]);

  useEffect(() => {
    loadOptions();
  }, []);

  const handleSearch = () => {
    setPage(1);
    loadData(1, pageSize);
  };

  const handleReset = () => {
    setKeyword('');
    setReceiveMethod(undefined);
    setApplyScope(undefined);
    setPage(1);
    setTimeout(() => loadData(1, pageSize), 0);
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue(defaultFormValues);
    setModalVisible(true);
  };

  const handleEdit = (record: ServiceCouponRecord) => {
    setEditingRecord(record);
    form.resetFields();
    form.setFieldsValue({
      ...defaultFormValues,
      ...record,
      categoryIds: record.categoryIds || [],
      productIds: record.productIds || [],
      fixedTimeRange: record.startAt && record.endAt ? [dayjs(record.startAt), dayjs(record.endAt)] : undefined,
    });
    setModalVisible(true);
  };

  const normalizeSubmitValues = (values: CouponFormValues): SaveServiceCouponRequest => {
    const payload: SaveServiceCouponRequest = {
      ...values,
      discountAmount: values.couponType === 'cash' ? Number(values.discountAmount || 0) : 0,
      discountRate: values.couponType === 'discount' ? Number(values.discountRate || 0) : 0,
      minOrderAmount: values.thresholdType === 'full' ? Number(values.minOrderAmount || 0) : 0,
      categoryIds: values.applyScope === 'category' ? values.categoryIds || [] : [],
      productIds: values.applyScope === 'product' ? values.productIds || [] : [],
      validDays: values.effectiveType === 'immediate' ? Number(values.validDays || 0) : 0,
      totalQuantity: Number(values.totalQuantity || 0),
      receivedQuantity: Number(values.receivedQuantity || 0),
      sort: Number(values.sort || 0),
      status: values.status ? 1 : 0,
    };

    if (values.effectiveType === 'fixed_time' && values.fixedTimeRange?.length === 2) {
      payload.startAt = values.fixedTimeRange[0].toISOString();
      payload.endAt = values.fixedTimeRange[1].toISOString();
    } else {
      payload.startAt = undefined;
      payload.endAt = undefined;
    }

    delete (payload as any).fixedTimeRange;
    return payload;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = normalizeSubmitValues(values);
      setSubmitLoading(true);
      if (editingRecord) {
        await updateServiceCoupon(editingRecord.id, payload);
        message.success('更新成功');
      } else {
        await createServiceCoupon(payload);
        message.success('创建成功');
      }
      setModalVisible(false);
      loadData();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || '保存失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteServiceCoupon(id);
      message.success('删除成功');
      loadData();
    } catch (error: any) {
      message.error(error?.response?.data?.message || '删除失败');
    }
  };

  const renderQuantity = (record: ServiceCouponRecord) => {
    const totalText = record.totalQuantity > 0 ? record.totalQuantity : '不限量';
    const remainingText = record.remainingQuantity >= 0 ? record.remainingQuantity : '不限量';
    return `已领 ${record.receivedQuantity} / 未领 ${remainingText} / 总量 ${totalText}`;
  };

  const renderDiscountContent = (record: ServiceCouponRecord) => (
    record.couponType === 'cash' ? `减免 ¥${record.discountAmount}` : `${record.discountRate} 折`
  );

  const renderThreshold = (record: ServiceCouponRecord) => (
    record.thresholdType === 'full' ? `满 ¥${record.minOrderAmount} 可用` : '无门槛'
  );

  const columns: ColumnsType<ServiceCouponRecord> = [
    { title: '名称', dataIndex: 'name', key: 'name', width: 180 },
    {
      title: '颜色',
      dataIndex: 'color',
      key: 'color',
      width: 90,
      render: (value: string) => <Tag color={couponColorOptions.find(item => item.value === value)?.color}>{couponColorMap[value] || value}</Tag>,
    },
    {
      title: '领取方式',
      dataIndex: 'receiveMethod',
      key: 'receiveMethod',
      width: 130,
      render: (value: string) => couponReceiveMethodMap[value] || value,
    },
    {
      title: '优惠券类型',
      dataIndex: 'couponType',
      key: 'couponType',
      width: 120,
      render: (value: string) => couponTypeMap[value] || value,
    },
    {
      title: '优惠内容',
      key: 'couponType',
      width: 130,
      render: (_, record) => renderDiscountContent(record),
    },
    {
      title: '使用门槛',
      key: 'thresholdType',
      width: 140,
      render: (_, record) => renderThreshold(record),
    },
    {
      title: '适用范围',
      dataIndex: 'applyScope',
      key: 'applyScope',
      width: 140,
      render: (value: string) => couponApplyScopeMap[value] || value,
    },
    {
      title: '领取情况',
      key: 'quantity',
      width: 260,
      render: (_, record) => renderQuantity(record),
    },
    {
      title: '有效期',
      key: 'effective',
      width: 220,
      render: (_, record) => (
        record.effectiveType === 'immediate'
          ? `领取后 ${record.validDays} 天`
          : `${record.startAt ? dayjs(record.startAt).format('YYYY-MM-DD') : '-'} 至 ${record.endAt ? dayjs(record.endAt).format('YYYY-MM-DD') : '-'}`
      ),
    },
    { title: '排序', dataIndex: 'sort', key: 'sort', width: 80 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (value: number) => <Tag color={value === 1 ? 'green' : 'red'}>{value === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除该优惠券吗？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={<><WalletOutlined /> 优惠券</>}
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增优惠券</Button>}
      >
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="优惠券名称"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
          <Select
            placeholder="领取方式"
            value={receiveMethod}
            onChange={setReceiveMethod}
            options={couponReceiveMethodOptions}
            style={{ width: 160 }}
            allowClear
          />
          <Select
            placeholder="适用商品"
            value={applyScope}
            onChange={setApplyScope}
            options={couponApplyScopeOptions}
            style={{ width: 180 }}
            allowClear
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
        </Space>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: t => `共 ${t} 条`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑优惠券' : '新增优惠券'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitLoading}
        width={980}
        destroyOnHidden
      >
        <Form form={form} layout="horizontal" labelCol={{ span: 3 }} wrapperCol={{ span: 21 }}>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="请输入名称" maxLength={100} />
          </Form.Item>

          <Form.Item name="color" label="优惠券颜色" rules={[{ required: true, message: '请选择优惠券颜色' }]}>
            <Radio.Group optionType="button" buttonStyle="outline" options={couponColorOptions.map(({ label, value }) => ({ label, value }))} />
          </Form.Item>

          <Form.Item name="receiveMethod" label="发放方式" rules={[{ required: true, message: '请选择发放方式' }]}>
            <Radio.Group optionType="button" buttonStyle="outline" options={couponReceiveMethodOptions} />
          </Form.Item>

          <Form.Item name="couponType" label="优惠券类型" rules={[{ required: true, message: '请选择优惠券类型' }]}>
            <Radio.Group optionType="button" buttonStyle="outline" options={couponTypeOptions} />
          </Form.Item>

          {currentCouponType === 'cash' ? (
            <Form.Item name="discountAmount" label="减免金额" rules={[{ required: true, message: '请输入减免金额' }]}>
              <InputNumber min={0.01} precision={2} style={{ width: '100%' }} placeholder="请输入减免金额" addonAfter="元" />
            </Form.Item>
          ) : (
            <Form.Item name="discountRate" label="折扣" rules={[{ required: true, message: '请输入折扣' }]}>
              <InputNumber min={0.1} max={9.99} precision={2} style={{ width: '100%' }} placeholder="例如 8.5 表示 8.5 折" addonAfter="折" />
            </Form.Item>
          )}

          <Form.Item name="thresholdType" label="使用门槛" rules={[{ required: true, message: '请选择使用门槛' }]}>
            <Radio.Group optionType="button" buttonStyle="outline" options={couponThresholdOptions} />
          </Form.Item>

          {currentThresholdType === 'full' && (
            <Form.Item name="minOrderAmount" label="门槛金额" rules={[{ required: true, message: '请输入门槛金额' }]}>
              <InputNumber min={0.01} precision={2} style={{ width: '100%' }} placeholder="请输入满多少可用" addonAfter="元" />
            </Form.Item>
          )}

          <Form.Item name="applyScope" label="适用商品" rules={[{ required: true, message: '请选择适用商品' }]}>
            <Radio.Group optionType="button" buttonStyle="outline" options={couponApplyScopeOptions} />
          </Form.Item>

          {currentApplyScope === 'category' && (
            <Form.Item name="categoryIds" label="指定品类" rules={[{ required: true, message: '请选择指定品类' }]}>
              <Select mode="multiple" options={categoryOptions} placeholder="请选择指定品类" />
            </Form.Item>
          )}

          {currentApplyScope === 'product' && (
            <Form.Item name="productIds" label="指定商品" rules={[{ required: true, message: '请选择指定商品' }]}>
              <Select mode="multiple" options={productOptions} placeholder="请选择指定商品" />
            </Form.Item>
          )}

          <Form.Item name="effectiveType" label="有效类型" rules={[{ required: true, message: '请选择有效类型' }]}>
            <Radio.Group optionType="button" buttonStyle="outline" options={couponEffectiveTypeOptions} />
          </Form.Item>

          {currentEffectiveType === 'immediate' ? (
            <Form.Item name="validDays" label="有效天数" rules={[{ required: true, message: '请输入有效天数' }]}>
              <InputNumber min={1} style={{ width: '100%' }} placeholder="请输入有效天数" />
            </Form.Item>
          ) : (
            <Form.Item name="fixedTimeRange" label="固定时间" rules={[{ required: true, message: '请选择固定时间' }]}>
              <RangePicker style={{ width: '100%' }} showTime />
            </Form.Item>
          )}

          <Form.Item name="totalQuantity" label="发放数量" extra="填 0 表示不限量">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入发放数量" />
          </Form.Item>

          <Form.Item name="receivedQuantity" label="已领取数量">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入已领取数量" />
          </Form.Item>

          <Form.Item name="sort" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="数字越小越靠前" />
          </Form.Item>

          <Form.Item name="status" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} maxLength={500} showCount placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ServiceCouponPage;
